import { Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogShellComponent } from '@custom-components';
import { FilledButtonComponent, TextButtonComponent } from '@custom-components/general/button';
import { TableComponent } from '@custom-components/general/table/table.component';
import { FilterGroup, TableFilterBarComponent } from '@custom-components/general/table-filter-bar';
import { COUNTRIES } from '@core/constants';
import { TableColumnConfig } from '@core/models/skeleton.model';
import { LocationModel } from '@core/models';
import { InstallationGroupService } from '@core/services';
import { InstallationGroupStore } from '@root/app/store/installation-group.store';
import { LocationStore } from '@root/app/store/location.store';

export interface AddLocationsDialogData {
  groupOid: string;
  /** OID of the partner that owns the group — its locations are the add candidates. */
  ownerPartnerOid: string;
}

type LocationRow = Record<string, unknown> & {
  locationId: number;
  address: string;
  locationPostalCode: string;
  locationCity: string;
  locationCountry: string;
};

/** Members are fetched once (across all pages) so already-grouped locations can be shown disabled. */
const MEMBER_FETCH_LIMIT = 1000;

@Component({
  selector: 'xemex-add-locations-dialog',
  imports: [
    TranslateModule,
    DialogShellComponent,
    FilledButtonComponent,
    TextButtonComponent,
    TableComponent,
    TableFilterBarComponent,
  ],
  providers: [LocationStore],
  templateUrl: './add-locations-dialog.component.html',
  styleUrl: './add-locations-dialog.component.scss',
})
export class AddLocationsDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<AddLocationsDialogComponent>);
  private readonly groupService = inject(InstallationGroupService);
  private readonly store = inject(InstallationGroupStore);
  private readonly translate = inject(TranslateService);
  protected readonly locationStore = inject(LocationStore);
  readonly data = inject<AddLocationsDialogData>(MAT_DIALOG_DATA);

  protected readonly columns: TableColumnConfig[] = [
    { field: 'address', label: 'locations.table.address', width: '34%', bold: true },
    { field: 'locationPostalCode', label: 'locations.table.zipcode', width: '18%' },
    { field: 'locationCity', label: 'locations.table.city', width: '24%' },
    { field: 'locationCountry', label: 'locations.table.country', width: '24%' },
  ];

  protected readonly filterGroups = computed<FilterGroup[]>(() => [
    {
      key: 'country',
      label: this.translate.instant('locations.filter.country'),
      options: COUNTRIES.map(c => c.id),
    },
  ]);

  /** Location ids already in the group — shown in the list but not selectable. */
  private readonly memberIds = signal<Set<number>>(new Set());
  /**
   * Members must resolve before any location rows are shown so an already-grouped location is
   * never briefly selectable before being disabled — the table stays loading until then.
   */
  private readonly membersLoaded = signal(false);
  protected readonly isLoading = computed(
    () => !this.membersLoaded() || this.locationStore.isLoading(),
  );
  /** Selected location ids; persists across server pages via the table's keyed selection. */
  protected readonly selectedKeys = signal<Set<string | number>>(new Set());
  protected readonly selectedCount = computed(() => this.selectedKeys().size);

  protected readonly selectionKey = (row: Record<string, unknown>): number => Number(row['locationId']);

  protected readonly tableData = computed<LocationRow[]>(() =>
    (this.locationStore.locationData()?.locations ?? []).map(location => this.toRow(location)),
  );

  /** Already-grouped locations are shown but not selectable — this dialog only adds, never removes. */
  protected readonly isMemberRow = (row: Record<string, unknown>): boolean =>
    this.memberIds().has(Number(row['locationId']));

  protected readonly totalCount = computed(
    () => this.locationStore.locationData()?.numberOfItems ?? 0,
  );

  constructor() {
    // Resolve the current members first, then browse the owner partner's locations server-side
    // (mirroring the locations page). Fetching locations only after members are known guarantees
    // the first rendered page already knows which rows to disable — they never flip once visible.
    this.groupService
      .getInstallationGroupLocations(this.data.groupOid, MEMBER_FETCH_LIMIT)
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: res => this.loadLocations(new Set((res.locations ?? []).map(m => m.locationId))),
        error: () => this.loadLocations(new Set()),
      });
  }

  private loadLocations(memberIds: Set<number>): void {
    this.memberIds.set(memberIds);
    this.membersLoaded.set(true);
    this.locationStore.getLocationsFilterMethod({ partnerOid: this.data.ownerPartnerOid });
  }

  protected onSearchChange(query: string): void {
    this.locationStore.getLocationsFilterMethod({ streetName: query });
  }

  protected onFilterChange(filters: Record<string, string[]>): void {
    this.locationStore.getLocationsFilterMethod({ country: filters['country']?.join(',') ?? '' });
  }

  protected onPageChange(event: PageEvent): void {
    this.locationStore.getLocationsMethod(event.pageIndex);
  }

  protected onSelectedKeysChange(keys: Set<string | number>): void {
    this.selectedKeys.set(keys);
  }

  protected onCancel(): void {
    this.dialogRef.close(false);
  }

  protected onAdd(): void {
    const locationIds = [...this.selectedKeys()].map(Number);
    if (!locationIds.length) {
      return;
    }
    this.store.addLocationsToGroup({ groupOid: this.data.groupOid, locationIds });
    this.dialogRef.close(true);
  }

  private toRow(location: LocationModel): LocationRow {
    return {
      locationId: location.locationId,
      address: this.locationAddress(location),
      locationPostalCode: location.locationPostalCode,
      locationCity: location.locationCity ?? '',
      locationCountry: location.locationCountry ?? '',
    };
  }

  private locationAddress(location: LocationModel): string {
    return (
      [location.locationStreet, location.locationStreetNumber, location.locationStreetNumberAddition]
        .filter(part => part !== null && part !== undefined && `${part}` !== '')
        .join(' ') || location.locationPostalCode
    );
  }
}
